<template>
  <modal
    width="560px"
    bodyPadding="0"
    :title="t('Enable Two-Factor Authentication')"
    @close="close"
    :readArticleUrl="`https://docs.bemob.com/${i18n.global.locale.value}/security`"
  >
    <div v-if="step === 1" class="wrapper">
      <div class="wrapper-container">
        <div class="pb-10">
          <ui-icon size="42" :color="colors.green" icon="fas fa-unlock-alt" />
        </div>
        <span class="mr-1">
          {{ t('For security please enter your password.') }}
          <template v-if="isMember">
            <br />
            {{ t('The 2FA can only be disabled by the account owner.') }}
          </template>
        </span>

        <ui-input
          v-model="form.password"
          autofocus
          class="pass-input"
          name="password"
          :label="t('Password')"
          :placeholder="t('Password')"
          :type="showPassword ? 'text' : 'password'"
          :append-inner-icon="showPassword ? 'fas fa-lock-open-alt' : 'fas fa-lock-alt'"
          :error-messages="fieldErrors.password"
          @change="clearFieldError('password')"
          @keyup.enter="submit"
          @click:appendInner="() => (showPassword = !showPassword)"
        />
      </div>
    </div>

    <template v-if="step === 2">
      <div class="p-24">
        <div>
          {{ t('1. Open Google Authenticator and scan QR-code') }}
        </div>
        <div class="d-flex flex-wrap__otp pb-4 mt-10 mb-16 align-items-center">
          <div class="mr-20">
            <img src="/images/google-otp.png" class="w100" alt="" />
          </div>
          <div class="mx-1">
            <img class="d-block w100" src="/images/google-play-button.png" alt="" />
          </div>
          <div class="mx-1">
            <img class="d-block w100" src="/images/appstore-button.png" alt="" />
          </div>
        </div>

        <div class="d-flex">
          <img
            v-if="qrPayload?.url"
            class="mr-20 code-image-wrp"
            :src="qrPayload.url"
            alt=""
          />
          <div class="ml-20">
            <span class="second-step d-inline-block mb-12">
              {{ t('2. Enter the code from Google Authenticator') }}
            </span>

            <ui-input
              v-model="form.token"
              class="pt-16"
              :label="t('Code')"
              placeholder="000000"
              autofocus
              mask="######"
              name="token"
              type="text"
              :error-messages="fieldErrors.token"
              @change="clearFieldError('token')"
              @keyup.enter="submit"
            />
          </div>
        </div>
      </div>
    </template>

    <template v-if="step === 3">
      <div class="etfa-wrap p-24">
        <div>
          <ui-icon size="42" :color="colors.green" icon="fa fa-check" />
        </div>
        <div class="mt-12">
          <h3>{{ t('Two-Factor Authentication is Enabled') }}</h3>
        </div>
        <div class="mt-12 mb-16">
          <span class="tip-enabled">
            {{
              t(
                'From now on, when you sign in to the website, you’ll need to enter a security code from your phone.',
              )
            }}
          </span>
        </div>
        <div>
          <h3>{{ t('Backup Codes') }}</h3>
        </div>
        <div class="mt-12 mb-16">
          <span
            class="tip-enabled"
            v-html="
              t(
                'You can use these one-time backup codes to access your account if you have no access to authenticator app.<br>Write these down and keep them safe.',
              )
            "
          />
        </div>
        <div class="d-flex flex-wrap">
          <div v-for="(code, index) in backupCodeList" :key="index" class="w-50">
            {{ code }}
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <section v-if="step === 1" class="footer xs12">
        <ui-button
          :text="t('Cancel')"
          icon="fas fa-times"
          :disabled="apiLoading"
          @click="close"
        />

        <ui-button
          class="ml-8"
          :text="t('Next Step')"
          icon-append="fas fa-arrow-right"
          button-style="accent"
          :loading="apiLoading"
          @click="submit"
        />
      </section>

      <section v-if="step === 2" class="footer xs12">
        <ui-button
          :text="t('Cancel')"
          icon="fas fa-times"
          :disabled="apiLoading"
          @click="close"
        />

        <ui-button
          class="ml-8"
          :text="t('Check Code')"
          icon-append="fas fa-arrow-right"
          button-style="accent"
          :loading="apiLoading"
          @click="submit"
        />
      </section>

      <section v-if="step === 3" class="footer xs12">
        <ui-button
          :text="t('Done')"
          icon="fas fa-check"
          button-style="accent"
          :loading="apiLoading"
          @click="handleDone"
        />
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { useI18n, useColors } from '@/composables';
import { useStore } from '@/store';
import { getUserFromApi } from '@/services/api/api-services/load-profile';
import i18n from '@/i18n';
import {
  postUserTwoFactorGetQrCodeApiRequest,
  postUserTwoFactorVerifyQrCodeApiRequest,
} from '@/services/api/user-api';
import type { FieldErrors } from '@/services/models';
import parseApiErrors from '@/utils/parseApiErrors';

const emit = defineEmits<{ close: [] }>();

const { t } = useI18n();
const colors = useColors();
const store = useStore();

const isMember = computed(() => store.user.isMember);

const step = ref(1);
const apiLoading = ref(false);
const showPassword = ref(false);
const fieldErrors = ref<FieldErrors>({});

interface QrPayload {
  url: string;
  backupCodes?: string[];
}

const qrPayload = ref<QrPayload | null>(null);
const backupCodeList = ref<string[]>([]);

const form = reactive({
  password: '',
  token: '',
});

const clearFieldError = (field: keyof FieldErrors) => {
  const newValue = { ...fieldErrors.value };
  delete newValue[field];
  fieldErrors.value = newValue;
};

const close = () => {
  emit('close');
};

const handleDone = () => {
  store.isAuthorized = true;
  apiLoading.value = true;
  getUserFromApi()
    .catch(() => {})
    .finally(() => {
      apiLoading.value = false;
      close();
    });
};

const submit = async () => {
  if (step.value === 1) {
    fieldErrors.value = {};
    apiLoading.value = true;
    try {
      const payload = (await postUserTwoFactorGetQrCodeApiRequest(form)) as QrPayload;
      qrPayload.value = payload;
      backupCodeList.value = payload.backupCodes ?? [];
      step.value = 2;
    } catch (error: unknown) {
      fieldErrors.value = parseApiErrors({ error, nested: true });
    } finally {
      apiLoading.value = false;
    }
    return;
  }

  if (step.value === 2) {
    const newValue = { ...fieldErrors.value };
    delete newValue.token;
    fieldErrors.value = newValue;
    apiLoading.value = true;
    try {
      const payload = (await postUserTwoFactorVerifyQrCodeApiRequest(form)) as {
        isTokenValid?: boolean;
      };
      if (payload.isTokenValid === false) {
        fieldErrors.value = {
          ...fieldErrors.value,
          token: [t('Invalid code')],
        };
        return;
      }
      step.value = 3;
    } catch (error: unknown) {
      fieldErrors.value = parseApiErrors({ error, nested: true });
    } finally {
      apiLoading.value = false;
    }
  }
};
</script>

<style lang="scss" scoped>

.wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}
.wrapper-container {
  width: 300px;
  padding: 24px;
}
.footer {
  display: flex;
  justify-content: flex-end;
}

.pass-input :deep(input) {
  padding-right: 20px;
}

.code-image-wrp {
  width: 41.666666666%;
}
</style>
